/** SUNTRONIX ONE — Quotation Template (Introduction & Closing) editor.
 *  Config Studio → Commercial → Quotation Template. Versioned rich-text copy
 *  used across all future quotations; issued quotes keep their frozen wording.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText, Plus, History, Star, Power, Copy, Eye, Bold, Italic,
  CornerDownLeft, Loader2, Save, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SCOPE_TABS = [{ id: "GLOBAL", label: "Global default (SUNTRONIX)" }, { id: "ESTABLISHMENT", label: "By establishment" }];

export default function QuotationTemplate() {
  const [meta, setMeta] = useState(null);
  const [scope, setScope] = useState("GLOBAL");
  const [estId, setEstId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const perms = meta?.permissions || [];
  const canEdit = perms.includes("edit");

  useEffect(() => {
    api.get("/quote-copy/meta").then(({ data }) => {
      setMeta(data);
      const nonPrimary = (data.establishments || []).find((e) => !e.is_primary);
      if (nonPrimary) setEstId(nonPrimary.id);
      else if (data.establishments?.[0]) setEstId(data.establishments[0].id);
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = scope === "ESTABLISHMENT" && estId ? `?scope=ESTABLISHMENT&establishment_id=${estId}` : "?scope=GLOBAL";
    api.get(`/quote-copy/templates${p}`).then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  }, [scope, estId]);
  useEffect(() => { load(); }, [load]);

  const estName = (id) => {
    const e = meta?.establishments?.find((x) => x.id === id);
    return e?.legal_name || e?.name || "Unnamed establishment (to be configured)";
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="quote-template-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-semibold">Configuration Studio · Commercial</div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><FileText className="h-6 w-6 text-indigo-600" /> Quotation Template</h1>
          <p className="text-sm text-slate-500 mt-1">The standard quotation introduction and closing message. Every save is a new version; issued quotes keep the exact wording used when they were issued.</p>
        </div>
        {meta && (
          <Badge className="bg-indigo-100 text-indigo-800"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> {perms.length} permissions</Badge>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 border-b border-slate-200">
          {SCOPE_TABS.map((t) => (
            <button key={t.id} onClick={() => setScope(t.id)} data-testid={`qt-scope-${t.id}`}
              className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px ${scope === t.id ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500"}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          {scope === "ESTABLISHMENT" && (
            <select value={estId} onChange={(e) => setEstId(e.target.value)} className="border rounded h-9 px-2 text-[13px]" data-testid="qt-est-select">
              {(meta?.establishments || []).map((e) => <option key={e.id} value={e.id}>{e.legal_name || e.name || "Unnamed establishment (to be configured)"}</option>)}
            </select>
          )}
          {canEdit && (
            <Button variant="create" onClick={() => setEditing({ scope, establishment_id: scope === "ESTABLISHMENT" ? estId : null, name: "", intro_html: "", closing_html: "" })}
              className="" data-testid="qt-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New template</Button>
          )}
        </div>
      </div>

      <div className="mt-4">
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : items.length === 0 ? (
          <div className="text-[13px] text-slate-400 border border-dashed rounded-lg py-10 text-center">No quotation copy defined for this scope yet.</div>
        ) : (
          <div className="grid gap-3">
            {items.map((t) => (
              <div key={t.group_id} className="border rounded-lg p-4 flex items-start justify-between gap-4" data-testid={`qt-card-${t.group_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[15px]">{t.name}</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">v{t.version}</span>
                    {t.is_default && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex items-center gap-0.5"><Star className="h-3 w-3" /> Default</span>}
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${t.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-400"}`}>{t.is_active ? "Active" : "Inactive"}</span>
                    {scope === "ESTABLISHMENT" && <span className="text-[11px] text-slate-500">· {estName(t.establishment_id)}</span>}
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1.5 line-clamp-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: t.intro_html || "<i>No introduction set</i>" }} />
                  {t.change_note && <div className="text-[11px] text-slate-400 mt-1">Last note: {t.change_note}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <IconBtn title="History" onClick={() => setHistoryFor(t.group_id)} testid={`qt-history-${t.group_id}`}><History className="h-4 w-4" /></IconBtn>
                  {canEdit && <IconBtn title="Edit" onClick={() => setEditing(t)} testid={`qt-edit-${t.group_id}`}><FileText className="h-4 w-4" /></IconBtn>}
                  {perms.includes("activate") && (
                    <>
                      <IconBtn title="Set default" onClick={() => act(t.group_id, "set-default", load)} testid={`qt-default-${t.group_id}`}><Star className="h-4 w-4" /></IconBtn>
                      <IconBtn title={t.is_active ? "Deactivate" : "Activate"} onClick={() => act(t.group_id, t.is_active ? "deactivate" : "activate", load)} testid={`qt-toggle-${t.group_id}`}><Power className="h-4 w-4" /></IconBtn>
                    </>
                  )}
                  {canEdit && <IconBtn title="Duplicate" onClick={() => act(t.group_id, "duplicate", load)} testid={`qt-dup-${t.group_id}`}><Copy className="h-4 w-4" /></IconBtn>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <EditorDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {historyFor && <HistoryDialog groupId={historyFor} canRestore={perms.includes("edit")} onClose={() => setHistoryFor(null)} onRestored={() => { setHistoryFor(null); load(); }} />}
    </div>
  );
}

async function act(gid, path, reload) {
  try { await api.post(`/quote-copy/templates/${gid}/${path}`); toast.success(path.replace("-", " ")); reload(); }
  catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
}

function IconBtn({ children, title, onClick, testid }) {
  return <button title={title} onClick={onClick} data-testid={testid} className="h-8 w-8 grid place-items-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50">{children}</button>;
}

/* ------------------------------------------------------------- Editor */
function EditorDialog({ initial, onClose, onSaved }) {
  const isNew = !initial.group_id;
  const [name, setName] = useState(initial.name || "");
  const [intro, setIntro] = useState(initial.intro_html || "");
  const [closing, setClosing] = useState(initial.closing_html || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    const payload = { name, scope: initial.scope, establishment_id: initial.establishment_id, intro_html: intro, closing_html: closing, change_note: note };
    try {
      if (isNew) await api.post("/quote-copy/templates", payload);
      else await api.put(`/quote-copy/templates/${initial.group_id}`, payload);
      toast.success(isNew ? "Template created (v1)" : "New version saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const doPreview = async () => {
    try { const { data } = await api.post("/quote-copy/preview", { intro_html: intro, closing_html: closing }); setPreview(data); }
    catch { toast.error("Preview failed"); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="qt-editor">
        <DialogHeader><DialogTitle>{isNew ? "New" : "Edit"} — {initial.scope === "GLOBAL" ? "Global (SUNTRONIX)" : "Establishment"} quotation copy</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="block"><div className="text-[11px] text-slate-500 mb-1">Template name</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="qt-name" /></label>

          <div>
            <div className="text-[12px] font-semibold text-slate-700 mb-1">Quotation Introduction</div>
            <div className="text-[11px] text-slate-400 mb-1">Shown below the greeting, above the line items. Bold words like OEM and SUNTRONIX render in bold.</div>
            <RichEditor value={intro} onChange={setIntro} testid="qt-intro" />
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-700 mb-1">Quotation Closing Message</div>
            <div className="text-[11px] text-slate-400 mb-1">Shown below the offer, before Terms &amp; Conditions and the signatory.</div>
            <RichEditor value={closing} onChange={setClosing} testid="qt-closing" />
          </div>

          <label className="block"><div className="text-[11px] text-slate-500 mb-1">Change note</div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for change" data-testid="qt-note" /></label>

          {preview !== null && (
            <div className="border rounded-lg p-3 bg-slate-50 space-y-3" data-testid="qt-preview">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Introduction preview</div>
                <div className="prose prose-sm max-w-none text-[13px]" dangerouslySetInnerHTML={{ __html: preview.intro_html }} data-testid="qt-preview-intro" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Closing preview</div>
                <div className="prose prose-sm max-w-none text-[13px]" dangerouslySetInnerHTML={{ __html: preview.closing_html }} data-testid="qt-preview-closing" />
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={doPreview} data-testid="qt-preview-btn"><Eye className="h-4 w-4 mr-1.5" /> Preview</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-indigo-700" data-testid="qt-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} {isNew ? "Create" : "Save new version"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RichEditor({ value, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || ""; }, []); // eslint-disable-line
  const cmd = (c) => { document.execCommand(c, false, null); ref.current?.focus(); onChange(ref.current?.innerHTML || ""); };
  return (
    <div>
      <div className="flex gap-1 mb-1">
        {[["bold", Bold], ["italic", Italic]].map(([c, Ic]) => (
          <button key={c} type="button" onMouseDown={(e) => { e.preventDefault(); cmd(c); }} className="h-7 w-7 grid place-items-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50"><Ic className="h-3.5 w-3.5" /></button>
        ))}
        <button type="button" title="Line break" onMouseDown={(e) => { e.preventDefault(); cmd("insertParagraph"); }} className="h-7 w-7 grid place-items-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50"><CornerDownLeft className="h-3.5 w-3.5" /></button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid={testid}
        onInput={() => onChange(ref.current?.innerHTML || "")}
        className="min-h-[90px] border rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-indigo-400 prose prose-sm max-w-none" />
    </div>
  );
}

/* ------------------------------------------------------------- History */
function HistoryDialog({ groupId, canRestore, onClose, onRestored }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/quote-copy/templates/${groupId}/versions`).then(({ data }) => setRows(data.items || []));
  useEffect(() => { load(); }, [groupId]); // eslint-disable-line
  const restore = async (v) => {
    try { await api.post(`/quote-copy/templates/${groupId}/restore/${v}`); toast.success(`Restored from v${v}`); onRestored?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qt-history-dialog">
        <DialogHeader><DialogTitle>Change history</DialogTitle></DialogHeader>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="border rounded-lg divide-y">
            {rows.map((r, idx) => (
              <div key={r.id} className="px-3 py-2 text-[12px] flex items-start justify-between gap-3" data-testid={`qt-ver-${r.version}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Version {r.version}{r.previous_version ? ` (from v${r.previous_version})` : ""}</span>
                    {idx === 0 && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Current</span>}
                    <span className="text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  <div className="text-slate-500">By {r.created_by} · {r.change_note || "—"}</div>
                </div>
                {canRestore && idx !== 0 && (
                  <Button size="sm" variant="outline" onClick={() => restore(r.version)} data-testid={`qt-restore-${r.version}`} className="shrink-0 h-7 text-[11px]">
                    Use this version
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
